import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from '@noor/lib';
import { isDeliveryForbiddenError } from '@/shared/lib/delivery-errors';
import { merchantChatApi, merchantChatKeys } from '@/entities/merchant-chat';

/**
 * Start a thread: create the chat (its subject is the first message, as in
 * the old composer) and send that message into it. Resolves with the new
 * chat's id so the drawer can open the thread.
 */
export function useStartMerchantChat() {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  return useMutation({
    // The error toast below replaces the global MutationCache one.
    meta: { suppressErrorToast: true },
    mutationFn: async ({ message, files }: { message: string; files: File[] }) => {
      const chat = await merchantChatApi.create(message || 'img');
      await merchantChatApi.send(chat.id, { message, files });
      return chat.id;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: merchantChatKeys.all });
    },
    onError: (error) => {
      if (isDeliveryForbiddenError(error)) return;
      toast.error(t('chat.toast.message_error'));
    },
  });
}
